import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../firebase';

const MFA_SESSION_KEY = 'dd_mfa_verified_uid';

let activeMfaResolver = null;
let activeMfaHints = null;

function getAuthErrorCode(error) {
  return error?.code || error?.customData?.code || '';
}

export { getAuthErrorCode };

export function isMfaRequiredError(error) {
  const code = getAuthErrorCode(error);
  return (
    code === 'auth/multi-factor-auth-required' ||
    String(error?.message || '').includes('multi-factor-auth-required')
  );
}

export function getActiveMfaSession() {
  if (!activeMfaResolver || !activeMfaHints?.length) {
    return null;
  }
  return {
    resolver: activeMfaResolver,
    hints: activeMfaHints,
  };
}

export function clearActiveMfaSession() {
  activeMfaResolver = null;
  activeMfaHints = null;
}

export function markMfaVerified(userId) {
  if (typeof window !== 'undefined' && userId) {
    sessionStorage.setItem(MFA_SESSION_KEY, userId);
  }
}

export function clearMfaVerified() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(MFA_SESSION_KEY);
  }
}

export function isMfaVerifiedForUser(userId) {
  if (typeof window === 'undefined' || !userId) {
    return false;
  }
  return sessionStorage.getItem(MFA_SESSION_KEY) === userId;
}

export function beginMfaSignIn(error) {
  if (!isMfaRequiredError(error)) {
    return null;
  }

  try {
    const resolver = getMultiFactorResolver(auth, error);
    const hints = resolver.hints || [];

    if (!hints.length) {
      throw new Error('No MFA method is configured for this account.');
    }

    activeMfaResolver = resolver;
    activeMfaHints = hints;

    return { resolver, hints };
  } catch (resolverError) {
    console.error('Failed to start MFA sign-in:', resolverError, error);
    throw new Error('Unable to start two-factor verification. Refresh the page and try again.');
  }
}

export async function signInWithEmail(email, password) {
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    clearActiveMfaSession();
    return { user: credential.user, mfaRequired: false };
  } catch (error) {
    const mfa = beginMfaSignIn(error);
    if (mfa) {
      return {
        mfaRequired: true,
        resolver: mfa.resolver,
        hints: mfa.hints,
      };
    }
    throw error;
  }
}

export function formatAuthError(error) {
  const code = getAuthErrorCode(error);

  if (isMfaRequiredError(error)) {
    return 'Two-factor authentication is required. Please complete verification below.';
  }
  if (code === 'auth/internal-error') {
    return 'Could not start SMS verification. Complete the reCAPTCHA below, then try again. If this persists, confirm Phone sign-in is enabled in Firebase Authentication.';
  }
  if (code === 'auth/captcha-check-failed') {
    return 'reCAPTCHA verification failed. Please try again.';
  }
  if (String(error?.message || '').includes('already been rendered')) {
    return 'reCAPTCHA is still loading. Please wait a moment and try again.';
  }
  if (code === 'auth/invalid-verification-code') {
    return 'Invalid verification code. Please check the code and try again.';
  }
  if (code === 'auth/missing-multi-factor-session' || code === 'auth/invalid-multi-factor-session') {
    return 'Your sign-in session expired. Close this window, sign in again, and retry MFA.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Wait a few minutes, then try again.';
  }
  return error.message || 'Authentication failed';
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

export async function initRecaptchaVerifier(containerId, { onSolved, onExpired, size = 'normal' } = {}) {
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error('reCAPTCHA container not found.');
  }

  if (container.childNodes.length > 0) {
    container.replaceChildren();
  }

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size,
    callback: () => {
      onSolved?.();
    },
    'expired-callback': () => {
      onExpired?.();
    },
  });

  await verifier.render();
  return verifier;
}

export function isRecaptchaError(error) {
  const code = getAuthErrorCode(error);
  return code === 'auth/captcha-check-failed' || code === 'auth/missing-recaptcha-token';
}

export function clearRecaptchaVerifier(verifier, containerId) {
  if (verifier) {
    try {
      verifier.clear();
    } catch {
      // ignore cleanup errors
    }
  }
  if (!containerId) return;
  const container = document.getElementById(containerId);
  if (container) {
    container.replaceChildren();
  }
}

export async function sendPhoneVerificationCode(resolver, hint, recaptchaVerifier) {
  if (!recaptchaVerifier) {
    throw new Error('reCAPTCHA has not been initialized.');
  }

  if (typeof recaptchaVerifier.verify === 'function') {
    await recaptchaVerifier.verify();
  }

  const phoneAuthProvider = new PhoneAuthProvider(auth);
  return withTimeout(
    phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: hint,
        session: resolver.session,
      },
      recaptchaVerifier
    ),
    45000,
    'Sending the verification code timed out. Check your connection and try again.'
  );
}

export async function completeMfaSignIn(resolver, hint, code, verificationId = null) {
  let assertion;

  if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
    if (!verificationId) {
      throw new Error('Phone verification has not been started.');
    }
    const cred = PhoneAuthProvider.credential(verificationId, code);
    assertion = PhoneMultiFactorGenerator.assertion(cred);
  } else if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
    assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
  } else {
    throw new Error('Unsupported MFA method. Use SMS or an authenticator app.');
  }

  const credential = await resolver.resolveSignIn(assertion);
  await credential.user.getIdToken(true);
  markMfaVerified(credential.user.uid);
  clearActiveMfaSession();
  return credential;
}

export function getMfaHintLabel(hint) {
  if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
    return hint.phoneNumber || 'Text message (SMS)';
  }
  if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
    return 'Authenticator app';
  }
  return 'Second factor';
}

export function isPhoneMfaHint(hint) {
  return hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

export function isTotpMfaHint(hint) {
  return hint.factorId === TotpMultiFactorGenerator.FACTOR_ID;
}
